import { useLocation, Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { TopNavbar } from "@/components/layout/TopNavbar";
import { Footer } from "@/components/layout/Footer";
import { Search, Home, MapPin } from "lucide-react";

const NotFound = () => {
  const location = useLocation();
  const { lang } = useParams<{ lang?: string }>();
  const homeHref = `/${lang || "en"}`;

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-secondary/20 to-accent/20">
      <TopNavbar />
      <main className="flex-1 flex items-center justify-center px-4 pt-20">
        <div className="w-full max-w-lg rounded-xl border bg-card text-card-foreground shadow-sm p-8 text-center">
          {/* Fun 404 number with gradient */}
          <div className="mb-6">
            <h1 className="text-8xl font-bold bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent animate-pulse">
              404
            </h1>
          </div>
          
          {/* Softer, more friendly icon */}
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-blue-500 animate-bounce">
            <Search className="h-8 w-8" />
          </div>
          
          <h2 className="text-2xl font-semibold mb-2">Oops! We're lost in space 🚀</h2>
          <p className="text-muted-foreground mb-6">
            Looks like this page took a detour to the moon. Don't worry, we'll get you back on track!
          </p>
          
          {/* Fun navigation options */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
            <Link 
              to={homeHref} 
              className="inline-flex items-center gap-2 justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 hover:scale-105 transition-transform"
            >
              <Home className="h-4 w-4" />
              Beam me home!
            </Link>
            <button 
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary h-10 px-4 py-2 hover:scale-105 transition-transform"
            >
              <MapPin className="h-4 w-4" />
              Go back
            </button>
          </div>
          
          {/* Fun facts section */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 font-medium mb-2">🚀 Fun fact:</p>
            <p className="text-xs text-blue-700">
              {Math.random() > 0.5 
                ? "404 errors are named after HTTP status codes, not because we're bad at directions!"
                : "The first 404 error was discovered in 1990. We've been getting lost ever since!"
              }
            </p>
          </div>
          
          {/* Page details in a more fun way */}
          <div className="mt-4 text-xs text-muted-foreground break-words text-left bg-muted/40 rounded-md p-3">
            <div className="font-medium mb-1 flex items-center gap-2">
              <MapPin className="h-3 w-3" />
              Lost coordinates
            </div>
            <div className="opacity-90 font-mono bg-background/50 px-2 py-1 rounded">
              {location.pathname}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;
